"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ArrowRight, Calendar, CircleCheck, FileText, MapPin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = ["Financial reporting support", "Program funding documentation", "Year-end reporting coordination", "Practical client follow-through"];
export default function PricingHero() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imgRef,
    isVisible: isImgVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="262744" id="job-posting-hero" className="relative bg-gradient-to-br from-white via-secondary-50/40 to-primary-50 py-20 lg:py-28 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(53,128,121,0.12),transparent_45%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div ref={textRef} className={`flex flex-col gap-8 transition-all duration-1000 ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="space-y-5">
              <span className="inline-flex items-center gap-2 text-secondary-700 font-default font-bold tracking-widest uppercase text-sm bg-secondary-100 px-4 py-2 rounded-md w-fit">
                <MapPin className="size-4" />
                Current Opportunity
              </span>

              <h1 className="text-4xl md:text-5xl lg:text-[var(--font-size-h1)] font-default font-bold leading-tight text-primary-900">
                Financial Reporting and Program Support Lead
              </h1>

              <p className="text-lg md:text-[var(--font-size-body)] text-primary-700 font-default max-w-xl leading-relaxed">
                Support First Nation communities and Northern organizations with
                practical financial reporting, program funding documentation,
                year-end reporting coordination, and steady follow-through
                across important client files.
              </p>
            </div>

            <ul className="grid sm:grid-cols-2 gap-4">
              {features.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                  <CircleCheck className="size-5 text-secondary-600 shrink-0 mt-0.5" />
                  <span className="text-primary-800 font-default font-medium">
                    {feature}
                  </span>
                </li>)}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <a href="mailto:employment@riverbendcorp.ca?subject=Application%20-%20Financial%20Reporting%20and%20Program%20Support%20Lead">
                <Button size="lg" className="w-full sm:w-auto h-14 px-8 text-base font-bold rounded-md shadow-md bg-secondary-600 text-white hover:bg-secondary-700 transition-all">
                  <SafeHydrate><Calendar className="mr-2 size-5" /></SafeHydrate>
                  Apply by Email
                </Button>
              </a>

              <Link to="/CareerOpportunitiesPage">
                <Button size="lg" variant="outline" className="w-full sm:w-auto h-14 px-8 text-base font-bold rounded-md border-primary-300 text-primary-900 bg-white hover:bg-primary-50 transition-all">
                  View All Opportunities
                  <ArrowRight className="ml-2 size-5" />
                </Button>
              </Link>
            </div>

            <div className="border-l-4 border-secondary-500 pl-5 py-1 max-w-xl">
              <p className="text-primary-700 font-default text-sm md:text-base leading-relaxed">
                This role is for someone who is organized, practical, and
                comfortable helping client teams keep financial reporting,
                program documentation, deadlines, and follow-up work moving
                forward.
              </p>
            </div>
          </div>

          <div ref={imgRef} className={`relative transition-all duration-1000 delay-300 ${isImgVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
            <div className="relative rounded-md overflow-hidden shadow-xl border border-primary-100 bg-white p-2">
              <AspectRatio ratio={4 / 3} className="bg-primary-50 rounded-md overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1624643/img/northern_financial_landscape.png" alt="Northern financial planning landscape representing financial reporting, program support, and client service" className="object-cover w-full h-full" loading="eager" />
              </AspectRatio>

              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md p-4 rounded-md shadow-lg border border-primary-100 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="size-12 rounded-md bg-secondary-100 flex items-center justify-center shrink-0">
                    <FileText className="size-6 text-secondary-700" />
                  </div>

                  <div>
                    <p className="text-primary-900 font-default font-bold text-sm md:text-base">
                      Financial Reporting Support
                    </p>
                    <p className="text-primary-600 font-default text-xs md:text-sm">
                      Full-time role with benefits
                    </p>
                  </div>
                </div>

                <a href="mailto:employment@riverbendcorp.ca?subject=Application%20-%20Financial%20Reporting%20and%20Program%20Support%20Lead" className="shrink-0">
                  <Button size="icon" variant="ghost" className="rounded-md hover:bg-secondary-100 hover:text-secondary-700">
                    <ArrowRight className="size-5" />
                  </Button>
                </a>
              </div>
            </div>

            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[115%] h-[115%] bg-secondary-200/30 blur-[120px] rounded-md pointer-events-none" />
          </div>
        </div>
      </div>
    </section>;
}